import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import Countdown from "@/components/Countdown";
import { api, money, formatError } from "@/lib/api";
import { track } from "@/lib/pixel";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

export default function Checkout() {
  const { slug } = useParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [course, setCourse] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", coupon_code: "" });
  const [couponInfo, setCouponInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [agree, setAgree] = useState(false);
  const [phonepe, setPhonepe] = useState({ enabled: false, env: "sandbox" });

  useEffect(() => {
    api.get(`/courses/${slug}`).then(({ data }) => setCourse(data));
    api.get("/payments/phonepe/config").then(({ data }) => setPhonepe(data)).catch(() => {});
  }, [slug]);

  useEffect(() => {
    if (user) setForm((f) => ({ ...f, name: user.name || "", email: user.email || "", phone: user.phone || "" }));
  }, [user]);

  useEffect(() => {
    if (course && course.course_id) {
      track("InitiateCheckout", {
        content_ids: [course.course_id],
        content_name: course.title,
        content_type: "product",
        value: course.current_price,
        currency: "INR",
        num_items: 1,
      });
    }
  }, [course]);

  const applyCoupon = async () => {
    try {
      const { data } = await api.post("/coupons/validate", { code: form.coupon_code, course_id: course.course_id });
      setCouponInfo(data);
      toast.success(`Coupon applied — save ${money(data.discount)}`);
    } catch (e) { setCouponInfo(null); toast.error(formatError(e)); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!agree) { toast.error("Please accept terms"); return; }
    setLoading(true);
    try {
      if (phonepe.enabled) {
        const { data } = await api.post("/payments/phonepe/initiate", { course_id: course.course_id, ...form });
        toast.success("Redirecting to PhonePe…");
        window.location.assign(data.redirect_url);
        return;
      }
      const { data } = await api.post("/checkout", { course_id: course.course_id, ...form });
      await refresh();
      toast.success("Payment successful!");
      nav(`/checkout/success/${data.order.order_id}`);
    } catch (err) { toast.error(formatError(err)); setLoading(false); }
  };

  if (!course) return <PublicLayout><div className="container-x py-32">Loading...</div></PublicLayout>;
  const finalPrice = couponInfo ? couponInfo.final_price : course.current_price;

  return (
    <PublicLayout>
      <section className="container-x pt-16 pb-24">
        <div className="overline mb-4">Secure checkout</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter mb-12">Complete your enrolment.</h1>
        <div className="grid lg:grid-cols-3 gap-10">
          <form onSubmit={submit} className="lg:col-span-2 space-y-6 glass rounded-2xl p-8" data-testid="checkout-form">
            <div className="grid md:grid-cols-2 gap-4">
              <Field label="Full name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="checkout-name" required />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="checkout-email" required />
              <Field label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="checkout-phone" required type="tel" pattern="[+0-9\s\-]{10,}" title="Please enter a valid phone number with at least 10 digits" />
            </div>
            <div>
              <div className="overline mb-2">Payment</div>
              {phonepe.enabled ? (
                <div className="glass rounded-xl p-4 text-sm text-ink-700 flex items-center gap-3" data-testid="checkout-phonepe-notice">
                  <div className="w-10 h-10 rounded-full bg-gold/15 flex items-center justify-center text-gold font-display text-lg">₹</div>
                  <div>
                    <div className="font-medium text-ink-900">PhonePe Secure Checkout</div>
                    <div className="text-xs text-ink-600">UPI · Cards · Netbanking · Wallets. You'll be redirected to PhonePe to complete payment securely.</div>
                  </div>
                </div>
              ) : (
                <div className="glass rounded-xl p-4 text-sm text-ink-600">Payments are disabled in demo. Clicking Complete Purchase will simulate a successful transaction and enrol you.</div>
              )}
            </div>
            <div className="flex items-start gap-3 text-sm text-ink-600">
              <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="checkout-terms" />
              <label>I accept the Terms & Refund policy.</label>
            </div>
            <button type="submit" disabled={loading} className="btn-neon w-full disabled:opacity-60" data-testid="checkout-submit">
              {loading ? "Processing..." : phonepe.enabled ? `Pay ${money(finalPrice)} with PhonePe` : `Complete Purchase — ${money(finalPrice)}`}
            </button>
          </form>

          <aside className="glass rounded-2xl p-6 h-fit space-y-4" data-testid="checkout-summary">
            <img src={course.thumbnail} alt={course.title} className="rounded-xl aspect-video object-cover w-full" />
            <div>
              <div className="overline mb-1">Course</div>
              <div className="font-display text-lg">{course.title}</div>
            </div>
            {course.offer?.ends_at && (
              <div>
                <div className="overline mb-2">Offer ends in</div>
                <Countdown endsAt={course.offer.ends_at} compact />
              </div>
            )}
            <div className="divider-x" />
            <Row l="Original price" v={money(course.original_price)} strike />
            <Row l="Price" v={money(course.current_price)} />
            {couponInfo && <Row l={`Coupon ${couponInfo.coupon.code}`} v={`- ${money(couponInfo.discount)}`} />}
            <div className="divider-x" />
            <Row l="Total" v={money(finalPrice)} bold />
            <div className="flex gap-2">
              <input value={form.coupon_code} onChange={(e) => setForm({ ...form, coupon_code: e.target.value })} placeholder="Coupon code" className="flex-1 rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-3 py-2 text-sm outline-none focus:border-neon" data-testid="checkout-coupon-input" />
              <button type="button" onClick={applyCoupon} className="btn-ghost !py-2 !px-4 text-sm" data-testid="checkout-coupon-apply">Apply</button>
            </div>
            <Link to={`/courses/${course.slug}`} className="block text-center text-xs text-ink-500 hover:text-ink-700">← Back to course</Link>
          </aside>
        </div>
      </section>
    </PublicLayout>
  );
}

const Field = ({ label, value, onChange, type = "text", required, testid, pattern, title }) => (
  <label className="block">
    <div className="overline mb-2">{label}{required ? <span className="text-gold ml-1">*</span> : null}</div>
    <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} pattern={pattern} title={title}
      className="w-full rounded-lg bg-ink-900/[0.04] border border-ink-900/10 px-4 py-3 outline-none focus:border-neon transition-colors" data-testid={testid} />
  </label>
);
const Row = ({ l, v, strike, bold }) => (
  <div className="flex items-center justify-between text-sm">
    <span className="text-ink-600">{l}</span>
    <span className={`${strike ? "line-through text-ink-500" : "text-ink-900"} ${bold ? "font-display text-lg" : ""}`}>{v}</span>
  </div>
);
